import { useState, useEffect } from 'react';
import { SpotlightActionData } from '@mantine/spotlight';
import { useDebouncedValue } from '@mantine/hooks';
import {
  SearchResult,
  SearchResponse,
  searchAPI,
  convertToSpotlightActions,
  createSeeAllResultsAction,
  shouldShowSeeAllAction,
  SEARCH_DEBOUNCE_TIME,
  getGroupName,
  getIconForType,
  getEventDescriptionColor,
} from '../utils/searchHelpers';

export const useSearch = () => {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActions, setSearchActions] = useState<SpotlightActionData[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showFullResults, setShowFullResults] = useState(false);
  const [fullSearchResults, setFullSearchResults] = useState<SearchResponse | null>(null);
  const [fullSearchLoading, setFullSearchLoading] = useState(false);

  const [debouncedQuery] = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_TIME);

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedQuery.trim()) {
      setIsSearching(true);
      searchAPI(debouncedQuery)
        .then((response) => {
          console.log('=== FRONTEND DEBUG ===');
          console.log('API Response for:', debouncedQuery, response);
          console.log('First few results:', response.results?.slice(0, 3));

          const actions = convertToSpotlightActions(response.results);
          console.log('Generated actions:', actions);
          console.log('Actions length:', actions.length);

          setSearchResults(response.results);
          setSearchActions(actions);
        })
        .finally(() => setIsSearching(false));
    } else {
      setSearchResults([]);
      setSearchActions([]);
    }
  }, [debouncedQuery]);

  // Handlers
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleShowAllResults = async () => {
    if (!searchQuery.trim()) return;

    setFullSearchLoading(true);
    setShowFullResults(true);

    try {
      const response = await searchAPI(searchQuery);
      setFullSearchResults(response);
    } finally {
      setFullSearchLoading(false);
    }
  };

  const handleBackToSearch = () => {
    setShowFullResults(false);
  };

  const closeFullResults = () => {
    setShowFullResults(false);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchActions([]);
    setFullSearchResults(null);
  };

  // Generate spotlight actions including "see all results"
  const spotlightActions: SpotlightActionData[] = [
    ...searchActions,
    ...(shouldShowSeeAllAction(searchQuery, searchActions.length > 0)
      ? [createSeeAllResultsAction(searchQuery, handleShowAllResults)]
      : []),
  ];

  return {
    // State
    searchQuery,
    searchActions,
    searchResults,
    isSearching,
    showFullResults,
    fullSearchResults,
    fullSearchLoading,
    spotlightActions,

    // Handlers
    handleSearchChange,
    handleShowAllResults,
    handleBackToSearch,
    closeFullResults,
    clearSearch,

    // Re-exported utilities for components that use this hook
    getGroupName,
    getIconForType,
    getEventDescriptionColor,
  };
};

// Re-export types for convenience
export type { SearchResult, SearchResponse };
